/**
 * EmptyStateIllustration Component
 * Created: 08-10-2025 11:27:08
 * Purpose: Enhanced empty states with illustrations for Grid and Floor Plan
 * 
 * Features:
 * - Multiple illustration types (no-rooms, no-assignments, no-floor)
 * - Icon-based illustrations (lightweight, scalable)
 * - Actionable CTAs
 * - Descriptive messages
 * - Accessibility support
 * 
 * @example
 * <EmptyStateIllustration
 *   type="no-rooms"
 *   title="No rooms found"
 *   description="Create your first room to get started"
 *   action={<Button onClick={handleCreate}>+ Add Room</Button>}
 * />
 */

import React from 'react';
import { Stack, Text, Box, ThemeIcon } from '@mantine/core';
import {
  IconBuilding,
  IconCalendar,
  IconMapPin,
  IconUsers,
  IconAlertCircle,
  IconFileOff
} from '@tabler/icons-react';

interface EmptyStateIllustrationProps {
  /**
   * Type of empty state
   */
  type: 'no-rooms' | 'no-assignments' | 'no-floor' | 'no-data' | 'no-doctors' | 'error';
  
  /**
   * Custom title (overrides default)
   */
  title?: string;
  
  /**
   * Custom description (overrides default)
   */
  description?: string;
  
  /**
   * Action button/element to display
   */
  action?: React.ReactNode;
  
  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Get illustration config based on type
 */
const getIllustrationConfig = (type: string) => {
  switch (type) {
    case 'no-rooms':
      return {
        icon: IconBuilding,
        color: 'blue',
        defaultTitle: 'No rooms found',
        defaultDescription: 'No rooms are available for this floor. Please add rooms to continue.',
      };
    case 'no-assignments':
      return {
        icon: IconCalendar,
        color: 'gray',
        defaultTitle: 'No assignments yet',
        defaultDescription: 'Create your first assignment to start scheduling doctors to rooms.',
      };
    case 'no-floor':
      return {
        icon: IconMapPin,
        color: 'yellow',
        defaultTitle: 'No floor selected',
        defaultDescription: 'Please select a floor from the dropdown to view the floor plan.',
      };
    case 'no-doctors':
      return {
        icon: IconUsers,
        color: 'teal',
        defaultTitle: 'No doctors available',
        defaultDescription: 'Add doctors to the system before creating assignments.',
      };
    case 'error':
      return {
        icon: IconAlertCircle,
        color: 'red',
        defaultTitle: 'Unable to load data',
        defaultDescription: 'An error occurred while fetching data. Please try again.',
      };
    default: // no-data
      return {
        icon: IconFileOff,
        color: 'gray',
        defaultTitle: 'No data available',
        defaultDescription: 'There is no data to display at this time.',
      };
  }
};

export const EmptyStateIllustration: React.FC<EmptyStateIllustrationProps> = ({
  type,
  title,
  description,
  action,
  size = 'md',
}) => {
  const config = getIllustrationConfig(type);
  const Icon = config.icon;
  
  const sizeConfig = {
    sm: { iconSize: 48, titleSize: 'lg', spacing: 'sm' },
    md: { iconSize: 64, titleSize: 'xl', spacing: 'md' },
    lg: { iconSize: 80, titleSize: '2xl', spacing: 'lg' },
  };
  
  const currentSize = sizeConfig[size];
  
  return (
    <Stack
      align="center"
      justify="center"
      gap={currentSize.spacing as any}
      style={{
        minHeight: '300px',
        padding: '48px 24px',
      }}
      role="status"
      aria-live="polite"
    >
      {/* Icon */}
      <ThemeIcon
        size={currentSize.iconSize}
        radius="xl"
        variant="light"
        color={config.color}
        style={{
          background: `linear-gradient(135deg, var(--mantine-color-${config.color}-0) 0%, var(--mantine-color-${config.color}-1) 100%)`,
          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.08)`,
        }}
      >
        <Icon size={currentSize.iconSize * 0.6} />
      </ThemeIcon>
      
      {/* Title */}
      <Text
        size={currentSize.titleSize as any}
        fw={700}
        c="dark"
        ta="center"
      >
        {title || config.defaultTitle}
      </Text>
      
      {/* Description */}
      <Text
        size="sm"
        c="dimmed"
        ta="center"
        maw={400}
        style={{ lineHeight: 1.6 }}
      >
        {description || config.defaultDescription}
      </Text>
      
      {/* Action */}
      {action && (
        <Box mt="md">
          {action}
        </Box>
      )}
    </Stack>
  );
};

export default EmptyStateIllustration;

